import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// This form is mounted on every page via the site-wide Footer, so it stays
// deliberately dependency-light: plain state + a hand-rolled email check
// instead of react-hook-form/zod. Those libraries (~67KB combined) are kept
// for actual multi-field forms on their own lazy-loaded pages (Contact,
// VisitorPassForm, VisitRequestForm) where the validation UX pays for its
// weight; here it would just inflate every visitor's initial bundle for a
// two-field newsletter signup. The server independently validates the same
// fields via insertEmailSignupSchema.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAIL_LENGTH = 254;
const MAX_LOCATION_LENGTH = 500;

export default function EmailSignup() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [locationSuggestion, setLocationSuggestion] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const submitSignupMutation = useMutation({
    mutationFn: async (data: { email: string; locationSuggestion?: string }) => {
      const response = await apiRequest('POST', '/api/email-signups', data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Thank you for signing up!",
        description: "You'll receive updates about Effortless Gym and new locations.",
      });
      setEmail("");
      setLocationSuggestion("");
      setEmailError(null);
    },
    onError: (error) => {
      console.error('Error submitting email signup:', error);
      toast({
        title: "Signup failed",
        description: "Please try again later or contact us directly.",
        variant: "destructive"
      });
    },
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !EMAIL_REGEX.test(trimmedEmail) || trimmedEmail.length > MAX_EMAIL_LENGTH) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setEmailError(null);

    submitSignupMutation.mutate({
      email: trimmedEmail,
      locationSuggestion: locationSuggestion.trim().slice(0, MAX_LOCATION_LENGTH) || undefined,
    });
  };

  return (
    <div>
      <p className="text-muted-foreground text-sm mb-3">
        Stay updated with news and new locations
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="email-signup-form">
        <div className="flex-1">
          <Input
            type="email"
            placeholder="Your Email"
            className="bg-background border-border text-sm h-9"
            data-testid="input-signup-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={MAX_EMAIL_LENGTH}
            required
          />
          {emailError && (
            <p className="text-destructive text-xs mt-1">{emailError}</p>
          )}
        </div>
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Where should we open next?"
            className="bg-background border-border text-sm h-9"
            data-testid="input-location-suggestion"
            value={locationSuggestion}
            onChange={(e) => setLocationSuggestion(e.target.value)}
            maxLength={MAX_LOCATION_LENGTH}
          />
        </div>
        <Button
          type="submit"
          className="bg-primary text-primary-foreground hover:bg-primary/90 text-sm h-9 px-6"
          disabled={submitSignupMutation.isPending}
          data-testid="button-signup-submit"
        >
          {submitSignupMutation.isPending ? "Signing Up..." : "Sign Up"}
        </Button>
      </form>
    </div>
  );
}
